import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { api, apiUrl, fmtINR, fmtCompact, fmtPeriod, periodOptions } from "@/lib/api";
import { PageHeader, Btn, Modal, Input, Select, SearchableSelect, Section, MonthPicker } from "@/components/Shared";
import { Plus, Pencil, Trash2, Upload, Search, ChevronDown, ChevronRight, Calendar } from "lucide-react";
import { toast } from "sonner";
import { openConfirm } from "@/lib/dialogs";

const EMPTY = { period: "", program_id: "", transaction_count: 0, processed_volume: 0, manual_adjustments: 0, notes: "", effective_date: "" };

// Default period = previous calendar month, computed at modal-open time so
// the year rolls forward automatically (e.g. on 2027-01-01 the default
// becomes 2026-12 without a code change). Empty string falls back if the
// computation ever fails — the form then surfaces the required-field error.
const prevMonthPeriod = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-indexed; current month
  // Walk back one month: Jan (m=0) → Dec previous year, otherwise same year.
  const py = m === 0 ? y - 1 : y;
  const pm = m === 0 ? 12 : m;
  return `${py}-${String(pm).padStart(2, "0")}`;
};

export default function VolumeInput() {
  const [vols, setVols] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [periodFilter, setPeriodFilter] = useState("");
  const [gatewayFilter, setGatewayFilter] = useState("");
  const [merchantFilter, setMerchantFilter] = useState("");  // matches program.merchant_id
  const [lobFilter, setLobFilter] = useState("");
  const [importing, setImporting] = useState(false);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState({ key: "period", dir: "desc" });
  // Show the "Advanced" section in the modal — collapsed by default. The
  // toggle reveals the optional Effective Date picker (PR-A.5 / Option B).
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const [v, p] = await Promise.all([api.get("/volumes"), api.get("/programs")]);
    setVols(v.data); setPrograms(p.data.sort((a, b) => a.program_id.localeCompare(b.program_id)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const periods = useMemo(() => [...new Set(vols.map((v) => v.period))].sort(), [vols]);
  const programOf = useCallback((pid) => programs.find((p) => p.program_id === pid), [programs]);

  // Distinct gateway / LOB / merchant lists, derived from `programs` so the
  // filter pickers stay in lock-step with what actually appears in the
  // dropdown when adding a volume entry.
  const gatewayOptions = useMemo(
    () => [...new Set(programs.map((p) => p.gateway).filter(Boolean))].sort(),
    [programs],
  );
  const lobOptions = useMemo(
    () => [...new Set(programs.map((p) => p.lob).filter(Boolean))].sort(),
    [programs],
  );
  const merchantOptions = useMemo(() => {
    const seen = new Map();
    for (const p of programs) {
      if (p.merchant_id && !seen.has(p.merchant_id)) {
        seen.set(p.merchant_id, { value: p.merchant_id, label: `${p.merchant_id} · ${p.merchant_name || ""}` });
      }
    }
    return [...seen.values()].sort((a, b) => a.value.localeCompare(b.value));
  }, [programs]);
  const hasStructuredFilter = !!(gatewayFilter || merchantFilter || lobFilter);

  const filtered = useMemo(() => {
    let rows = periodFilter ? vols.filter((v) => v.period === periodFilter) : [...vols];
    // Structured filters (gateway / merchant / lob) — applied via the
    // associated program. Cheaper to check these before the freeform search.
    if (hasStructuredFilter) {
      rows = rows.filter((v) => {
        const p = programOf(v.program_id);
        if (gatewayFilter && p?.gateway !== gatewayFilter) return false;
        if (merchantFilter && p?.merchant_id !== merchantFilter) return false;
        if (lobFilter && p?.lob !== lobFilter) return false;
        return true;
      });
    }
    if (q) {
      const needle = q.toLowerCase();
      rows = rows.filter((v) => {
        const p = programOf(v.program_id);
        return [p?.merchant_id, p?.merchant_name, p?.gateway, p?.lob, v.notes, v.period]
          .some((s) => (s || "").toLowerCase().includes(needle));
      });
    }
    const { key, dir } = sortBy;
    const getVal = (v) => {
      const p = programOf(v.program_id);
      switch (key) {
        case "period": return v.period;
        case "merchant_id": return p?.merchant_id || "";
        case "merchant_name": return p?.merchant_name || "";
        case "gateway_lob": return `${p?.gateway || ""}·${p?.lob || ""}`;
        case "transaction_count": return v.transaction_count || 0;
        case "processed_volume": return v.processed_volume || 0;
        case "manual_adjustments": return v.manual_adjustments || 0;
        default: return "";
      }
    };
    rows.sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (typeof va === "number" && typeof vb === "number") return dir === "asc" ? va - vb : vb - va;
      return dir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return rows;
  }, [vols, periodFilter, q, sortBy, programOf, gatewayFilter, merchantFilter, lobFilter, hasStructuredFilter]);

  const totals = useMemo(() => filtered.reduce(
    (a, v) => ({
      transaction_count: a.transaction_count + (Number(v.transaction_count) || 0),
      processed_volume: a.processed_volume + (Number(v.processed_volume) || 0),
      manual_adjustments: a.manual_adjustments + (Number(v.manual_adjustments) || 0),
    }),
    { transaction_count: 0, processed_volume: 0, manual_adjustments: 0 },
  ), [filtered]);

  const toggleSort = (k) => setSortBy((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }));
  const SortTh = ({ k, children, align }) => (
    <th className={`${align === "right" ? "text-right" : ""} cursor-pointer select-none`} onClick={() => toggleSort(k)}>
      <span className="inline-flex items-center gap-1">{children}{sortBy.key === k && <span className="text-[9px]">{sortBy.dir === "asc" ? "▲" : "▼"}</span>}</span>
    </th>
  );

  const openNew = () => {
    // Smart default: previous calendar month, blank merchant + 0 volume so
    // the required-field validation surfaces if the operator hits Create
    // without picking. Year auto-rolls forward each January 1.
    setForm({ ...EMPTY, period: prevMonthPeriod() });
    setEditId(null);
    setAdvancedOpen(false);
    setOpen(true);
  };
  const openEdit = (v) => {
    setForm({ ...EMPTY, ...v, effective_date: v.effective_date || "" });
    setEditId(v.id);
    // Auto-open Advanced section when row already has an effective_date set
    setAdvancedOpen(Boolean(v.effective_date));
    setOpen(true);
  };

  const save = async () => {
    // Required-field validation — prevents accidental zero-volume / blank-
    // merchant rows that have silently slipped through before. Period
    // format is "YYYY-MM" emitted by MonthPicker (or "" when either
    // dropdown is blank).
    const missing = [];
    const period = (form.period || "").trim();
    if (!period || !/^\d{4}-\d{2}$/.test(period)) missing.push("Period");
    if (!form.program_id) missing.push("Merchant");
    const vol = Number(form.processed_volume);
    if (!Number.isFinite(vol) || vol <= 0) missing.push("Total Volume");
    if (missing.length) {
      toast.error(`Please fill: ${missing.join(", ")}`);
      return;
    }
    try {
      const payload = {
        ...form,
        transaction_count: Number(form.transaction_count),
        processed_volume: Number(form.processed_volume),
        manual_adjustments: Number(form.manual_adjustments || 0),
        // Empty string is meaningful — sends null so backend stores
        // explicit "no override". Backend then anchors to last day of period.
        effective_date: (form.effective_date && form.effective_date.trim()) ? form.effective_date.trim() : null,
      };
      if (editId) await api.put(`/volumes/${editId}`, payload); else await api.post("/volumes", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!await openConfirm({ title: "Delete volume entry?", message: "This will remove the recorded volume for the period. Any commission already calculated against it will be revised.", confirmLabel: "Delete entry", danger: true })) return;
    try { await api.delete(`/volumes/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (fileRef.current) fileRef.current.value = "";  // reset early so the same file can be re-picked

    // Iter31.4 — give the operator a clear choice before the upload runs.
    // "Append" (default, safe) updates rows in place keyed by
    //   (period, merchant, effective_date) and inserts new ones.
    // "Replace" wipes EVERY existing row for the periods present in the
    //   upload (open periods only — closed periods stay locked) and starts
    //   fresh from the file. Useful when the upload IS the authoritative
    //   end-of-month extract and you want to drop any manual ad-hoc
    //   tweaks that happened in between.
    const choice = await openConfirm({
      title: "How should we handle existing rows?",
      message:
        `Pick how this upload should treat existing volume rows in the period(s) found in "${file.name}":\n\n` +
        `• Append (recommended) — keep existing rows; update them in place when (period, merchant, effective date) matches; add new rows for new triples.\n\n` +
        `• Replace — DELETE every open-period volume row for the periods in this file before inserting, so the file becomes the single source of truth. Closed periods are not touched.`,
      confirmLabel: "Replace all rows for these periods",
      cancelLabel: "Append (recommended)",
      danger: true,
      // openConfirm returns true on confirm, false/undefined on cancel.
      // We use the cancel branch to mean "append" (the safe default), and
      // the confirm branch to mean "replace" (the destructive option).
    });
    const mode = choice ? "replace" : "append";

    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const { data } = await api.post("/import/volumes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const head = mode === "replace"
        ? `Replaced & imported: +${data.inserted} new, ${data.skipped} skipped`
        : `Imported: +${data.inserted} new, ~${data.updated} updated, ${data.skipped} skipped`;
      toast.success(head);
      if (mode === "replace" && data.replaced_periods?.length) {
        toast.info(`Cleared ${data.replaced_periods.length} period(s) before import`, {
          description: data.replaced_periods.slice(0, 4).join(" · "),
          duration: 8000,
        });
      }
      if (data.errors?.length) toast.warning(`${data.errors.length} note(s)`, { description: data.errors.slice(0, 3).join(" · "), duration: 9000 });
      // PR-D: surface volume rows back-posted past a deactivation date as
      // an amber warning toast — not blocking, finance has legit reasons
      // to back-post (chargeback adjustments, GST corrections), but the
      // operator should know.
      if (data.deactivated_warnings?.length) {
        toast.warning(`${data.deactivated_warnings.length} deactivated MID(s) had volume uploaded`, {
          description: data.deactivated_warnings.slice(0, 3).join(" · "),
          duration: 9000,
        });
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    setImporting(false);
  };

  // Iter31.5 — Bulk-delete every volume row in the currently-selected
  // period filter. Closed periods are blocked server-side. Useful for
  // resetting a botched import — pairs with the new "Replace" upload
  // mode but operates without re-uploading anything.
  // Iter31.6 — Now optionally narrows the wipe to a specific effective_date
  // present in the period so finance can surgically remove one bad upload
  // without losing the rest.
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteEff, setBulkDeleteEff] = useState("");  // "" = all dates
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);

  // Build the list of effective_dates present in the currently-filtered
  // period so the dialog can offer a clean dropdown. Includes a synthetic
  // "(no effective date)" option for legacy rows without an explicit anchor.
  const effectiveDateOptionsForPeriod = useMemo(() => {
    if (!periodFilter) return [];
    const seen = new Map();  // value → count
    let hasLegacy = false;
    for (const v of vols) {
      if (v.period !== periodFilter) continue;
      const ed = v.effective_date;
      if (!ed) { hasLegacy = true; continue; }
      seen.set(ed, (seen.get(ed) || 0) + 1);
    }
    const opts = [...seen.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([d, n]) => ({ value: d, label: `${d}  ·  ${n} row${n === 1 ? "" : "s"}` }));
    if (hasLegacy) opts.push({ value: "none", label: "(No effective date — legacy rows)" });
    return opts;
  }, [vols, periodFilter]);

  const openBulkDeleteDialog = () => {
    if (!periodFilter) {
      toast.warning("Pick a period in the filter first", { description: "Bulk delete operates on the currently-selected period." });
      return;
    }
    setBulkDeleteEff("");  // default to "all dates"
    setBulkDeleteOpen(true);
  };

  const submitBulkDelete = async () => {
    setBulkDeleteBusy(true);
    try {
      const params = bulkDeleteEff ? { effective_date: bulkDeleteEff } : {};
      const { data } = await api.delete(`/volumes/period/${periodFilter}`, { params });
      const scope = bulkDeleteEff
        ? (bulkDeleteEff === "none" ? "with no effective date" : `effective ${bulkDeleteEff}`)
        : `(all effective dates)`;
      toast.success(`Deleted ${data.deleted} row${data.deleted === 1 ? "" : "s"} from ${fmtPeriod(periodFilter)} ${scope}`);
      setBulkDeleteOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
    setBulkDeleteBusy(false);
  };

  return (
    <div data-testid="volume-page">
      <PageHeader
        title="Volume Input"
        subtitle="Upload Monthly Volume Data"
        actions={<>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" onChange={onUpload} className="hidden" data-testid="volume-file-input" />
          <Btn variant="ghost" testid="xlsx-template-btn" onClick={() => window.open(apiUrl("/import/volumes/template"), "_blank")}>Template</Btn>
          <Btn variant="ghost" testid="import-xlsx-btn" disabled={importing} onClick={() => fileRef.current?.click()}>
            <Upload size={14} /> {importing ? "Importing…" : "Import .xlsx"}
          </Btn>
          <div className="relative w-56">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
            <Input placeholder="Search merchant / gateway…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="volume-search" className="pl-8" />
          </div>
          <div className="w-44"><Select placeholder="All periods" options={periodOptions(periods)} value={periodFilter} onChange={setPeriodFilter} testid="period-filter" /></div>
          {periodFilter && (
            <Btn
              variant="ghost"
              testid="bulk-delete-period-btn"
              onClick={openBulkDeleteDialog}
              title={`Delete volume rows for ${fmtPeriod(periodFilter)}`}
              className="text-[var(--negative)] hover:bg-red-50"
            >
              <Trash2 size={14} /> Delete period
            </Btn>
          )}
          <Btn variant="accent" testid="add-volume-btn" onClick={openNew}><Plus size={14} /> Add Volume</Btn>
        </>}
      />
      <div className="p-4 sm:p-10">
        <Section title={`${filtered.length} ${filtered.length === 1 ? "entry" : "entries"}${q ? ` matching "${q}"` : ""}`} testid="volumes-section">
          {/* Structured filter bar — sits inside the section so it visually
              groups with the table it filters, and stays out of the page
              header (which is already crowded with import + period + add). */}
          <div className="flex flex-wrap items-end gap-3 px-4 sm:px-5 pt-4 pb-3 border-b border-[var(--line)] bg-[var(--surface-2)]" data-testid="volume-filter-bar">
            <div className="flex flex-col gap-1 min-w-[160px]">
              <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold">Payment Gateway</label>
              <Select
                testid="filter-gateway"
                placeholder="All gateways"
                options={gatewayOptions}
                value={gatewayFilter}
                onChange={setGatewayFilter}
              />
            </div>
            <div className="flex flex-col gap-1 w-[280px]">
              <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold">Merchant</label>
              <SearchableSelect
                testid="filter-merchant"
                placeholder="All merchants"
                searchPlaceholder="Search merchant id or name…"
                options={merchantOptions}
                value={merchantFilter}
                onChange={setMerchantFilter}
              />
            </div>
            <div className="flex flex-col gap-1 min-w-[160px]">
              <label className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold">Line of Business</label>
              <Select
                testid="filter-lob"
                placeholder="All LOBs"
                options={lobOptions}
                value={lobFilter}
                onChange={setLobFilter}
              />
            </div>
            {hasStructuredFilter && (
              <button
                type="button"
                data-testid="filter-clear"
                onClick={() => { setGatewayFilter(""); setMerchantFilter(""); setLobFilter(""); }}
                className="h-9 px-3 text-[12px] font-medium text-[var(--muted)] hover:text-[var(--ink)] hover:bg-[var(--surface)] border border-transparent hover:border-[var(--line-2)] rounded-sm"
              >Clear filters</button>
            )}
          </div>
          <div className="overflow-auto">
            <div className="overflow-x-auto"><table className="table-data w-full">
              <thead><tr>
                <SortTh k="period">Period</SortTh>
                <SortTh k="merchant_id">Merchant ID</SortTh>
                <SortTh k="merchant_name">Merchant</SortTh>
                <SortTh k="gateway_lob">Gateway / LOB</SortTh>
                <SortTh k="transaction_count" align="right">Txn Count</SortTh>
                <SortTh k="processed_volume" align="right">Total Volume</SortTh>
                <SortTh k="manual_adjustments" align="right">Manual Adj.</SortTh>
                <th>Notes</th>
                <th></th>
              </tr></thead>
              <tbody>
                {filtered.map((v) => {
                  const p = programOf(v.program_id);
                  return (
                    <tr key={v.id} data-testid={`volume-row-${v.id}`}>
                      <td className="num font-medium">
                        <div className="flex items-center gap-1.5">
                          <span>{fmtPeriod(v.period)}</span>
                          {v.effective_date && (
                            <span
                              title={`Rate-version anchor: ${v.effective_date}`}
                              className="text-[9.5px] bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-sm font-semibold tracking-wider whitespace-nowrap"
                              data-testid={`vol-eff-badge-${v.id}`}
                            >
                              {v.effective_date}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="num">{p?.merchant_id || "—"}</td>
                      <td className="font-medium">{p?.merchant_name || "—"}</td>
                      <td className="text-[12px] text-[var(--ink-2)]">{p?.gateway} · {p?.lob}</td>
                      <td className="text-right num">{fmtINR(v.transaction_count, 0)}</td>
                      <td className="text-right num font-medium">₹ {fmtINR(v.processed_volume, 0)}</td>
                      <td className={`text-right num ${v.manual_adjustments < 0 ? "text-[var(--negative)]" : ""}`}>{v.manual_adjustments ? fmtINR(v.manual_adjustments, 0) : "—"}</td>
                      <td className="text-[11px] text-[var(--muted)] max-w-[220px] truncate">{v.notes}</td>
                      <td className="text-right">
                        <button onClick={() => openEdit(v)} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm"><Pencil size={13} /></button>
                        <button onClick={() => remove(v.id)} className="p-1.5 hover:bg-red-50 text-[var(--negative)] rounded-sm"><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  );
                })}
                {!filtered.length && <tr><td colSpan={9} className="text-center text-[var(--muted)] py-6">No matches.</td></tr>}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-[var(--ink-2)] bg-[var(--surface-2)] font-semibold" data-testid="volumes-total-row">
                    <td colSpan={4} className="text-[11px] uppercase tracking-wider text-[var(--ink-2)]">
                      Totals · {filtered.length} {filtered.length === 1 ? "entry" : "entries"}{periodFilter ? ` · ${fmtPeriod(periodFilter)}` : ""}
                    </td>
                    <td className="text-right num" data-testid="totals-txn">{fmtINR(totals.transaction_count, 0)}</td>
                    <td className="text-right num" data-testid="totals-volume">₹ {fmtINR(totals.processed_volume, 0)}</td>
                    <td className={`text-right num ${totals.manual_adjustments < 0 ? "text-[var(--negative)]" : ""}`} data-testid="totals-adj">{totals.manual_adjustments ? fmtINR(totals.manual_adjustments, 0) : "—"}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table></div>
          </div>
        </Section>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Volume" : "Add Volume Entry"} testid="volume-modal">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Period <span className="text-[var(--negative)]">*</span></label><MonthPicker testid="form-period" value={form.period} onChange={(v) => setForm({ ...form, period: v })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Merchant <span className="text-[var(--negative)]">*</span></label>
            <SearchableSelect testid="form-vol-program" options={programs.map((p) => ({ value: p.program_id, label: `${p.merchant_id} · ${p.merchant_name} · ${p.gateway} · ${p.lob}` }))} value={form.program_id} onChange={(v) => setForm({ ...form, program_id: v })} placeholder="Select merchant" searchPlaceholder="Search by merchant id, name, gateway, LOB…" /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Transaction Count</label><Input type="number" step="0.01" data-testid="form-txn" value={form.transaction_count} onChange={(e) => setForm({ ...form, transaction_count: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Total Volume (₹) <span className="text-[var(--negative)]">*</span></label><Input type="number" step="1" data-testid="form-vol" value={form.processed_volume} onChange={(e) => setForm({ ...form, processed_volume: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Manual Adjustments</label><Input type="number" step="1" value={form.manual_adjustments} onChange={(e) => setForm({ ...form, manual_adjustments: e.target.value })} /></div>
          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Notes</label><Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>

        {/* Advanced — Optional Effective Date for rate-version anchoring (Option B / PR-A.5).
            Hidden by default; auto-opens on edit when the row already has a value set. */}
        <div className="mt-4 border-t border-[var(--line)] pt-3">
          <button
            type="button"
            onClick={() => setAdvancedOpen((s) => !s)}
            className="inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-wider font-semibold text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
            data-testid="vol-advanced-toggle"
          >
            {advancedOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            Advanced
            {form.effective_date && <span className="ml-1.5 text-[9.5px] bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-sm">Override active</span>}
          </button>
          {advancedOpen && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="vol-advanced-section">
              <div>
                <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider flex items-center gap-1.5">
                  <Calendar size={11} /> Effective Date <span className="text-[10px] normal-case text-[var(--muted)] ml-1">(optional)</span>
                </label>
                <Input
                  type="date"
                  value={form.effective_date || ""}
                  onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                  data-testid="form-vol-effective-date"
                />
                <div className="text-[10.5px] text-[var(--muted)] mt-1 leading-snug">
                  Pegs this row to the rate version active on the chosen date. Leave blank to use the <b>last day of {form.period || "the period"}</b> by default — picks the latest active rate within the month.
                </div>
                {form.effective_date && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, effective_date: "" })}
                    className="text-[10.5px] text-[var(--accent)] hover:underline mt-1.5"
                    data-testid="form-vol-clear-effective-date"
                  >
                    Clear override (use period default)
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn variant="accent" testid="save-volume" onClick={save}>{editId ? "Save" : "Create"}</Btn>
        </div>
      </Modal>

      {/* Iter31.6 — Bulk-delete dialog with optional effective_date scope */}
      <Modal
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        title={periodFilter ? `Delete volume rows · ${fmtPeriod(periodFilter)}` : "Delete volume rows"}
        testid="bulk-delete-modal"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-[13px] text-[var(--ink-2)] leading-relaxed">
            Choose how broadly to wipe volume entries. Closed periods are skipped automatically.
            <br />
            <span className="text-[var(--negative)] font-semibold">This cannot be undone.</span>
          </p>

          <div>
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Scope</label>
            <Select
              testid="bulk-delete-effdate-select"
              placeholder={`All effective dates in ${periodFilter ? fmtPeriod(periodFilter) : "the period"}`}
              options={effectiveDateOptionsForPeriod}
              value={bulkDeleteEff}
              onChange={setBulkDeleteEff}
            />
            <div className="text-[11.5px] text-[var(--muted)] mt-1.5 leading-relaxed">
              {bulkDeleteEff === ""
                ? <>Delete <strong>every</strong> volume row in {periodFilter ? fmtPeriod(periodFilter) : "this period"} — across all merchants, gateways, and effective dates.</>
                : bulkDeleteEff === "none"
                ? <>Delete only legacy rows that <strong>don't</strong> have an effective date set.</>
                : <>Delete only rows whose <strong>effective_date = {bulkDeleteEff}</strong>. Other dates in the period stay intact.</>}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 text-amber-900 text-[12px] px-3 py-2 rounded-sm">
            Calculations and Partner Payouts for the affected slice will recompute to zero until new volumes are uploaded.
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Btn variant="ghost" onClick={() => setBulkDeleteOpen(false)} testid="bulk-delete-cancel">Cancel</Btn>
            <Btn
              variant="danger"
              onClick={submitBulkDelete}
              disabled={bulkDeleteBusy}
              testid="bulk-delete-confirm"
            >
              <Trash2 size={14} />
              {bulkDeleteBusy
                ? "Deleting…"
                : bulkDeleteEff
                ? `Delete (${bulkDeleteEff === "none" ? "no eff. date" : bulkDeleteEff})`
                : "Delete all rows"}
            </Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
